import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import { lazyWithReload } from '@/shared/ui/lazyWithReload';
import { ProtectedRoute, PublicRoute } from './ProtectedRoute';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import RouteError from './RouteError';
import { PERMISSIONS } from '@/features/auth/permissions';
import LoginLayout from '@/features/auth/screens/LoginLayout';
import { store } from '@/shared/store';
import {
  setEnterpriseTypes,
  setLoginMethods,
} from '@/shared/store/slices/onboardingSlice';
import {
  setSkipTraceId,
  resetSkipTraceId,
} from '@/shared/store/slices/apiSlice';

// Comment for tag
// Lazy load route components
const EnterpriseManagement = lazyWithReload(
  () => import('@/features/enterprise/screens/EnterpriseManagement')
);
const EnterpriseDetails = lazyWithReload(
  () => import('@/features/enterprise/screens/EnterpriseDetails')
);
const EnterpriseOnboarding = lazyWithReload(
  () => import('@/features/enterprise/screens/EnterpriseOnboarding')
);
const IdentityVaultDetails = lazyWithReload(
  () => import('@/features/AssetIdentityVault/screens/IdentityVaultDetails')
);
const OverviewTabWrapper = lazyWithReload(() =>
  import('@/features/AssetIdentityVault/components/identityDrilldown/TabWrappers').then(
    (m) => ({ default: m.OverviewTabWrapper })
  )
);
const FullMetricsTabWrapper = lazyWithReload(() =>
  import('@/features/AssetIdentityVault/components/identityDrilldown/TabWrappers').then(
    (m) => ({ default: m.FullMetricsTabWrapper })
  )
);
const LiveMetricsTabWrapper = lazyWithReload(() =>
  import('@/features/AssetIdentityVault/components/identityDrilldown/TabWrappers').then(
    (m) => ({ default: m.LiveMetricsTabWrapper })
  )
);
const ManualAuthenticationTabWrapper = lazyWithReload(() =>
  import('@/features/AssetIdentityVault/components/identityDrilldown/TabWrappers').then(
    (m) => ({ default: m.ManualAuthenticationTabWrapper })
  )
);
const IsolatedMetricsTabWrapper = lazyWithReload(() =>
  import('@/features/AssetIdentityVault/components/identityDrilldown/TabWrappers').then(
    (m) => ({ default: m.IsolatedMetricsTabWrapper })
  )
);
const AdminCenterPage = lazyWithReload(
  () => import('@/features/adminCenter/components/adminCenterPage')
);
const UserManagementPage = lazyWithReload(
  () => import('@/features/adminCenter/screens/UserManagementPage')
);
const RoleManagementPage = lazyWithReload(
  () => import('@/features/adminCenter/screens/RoleManagementPage')
);
const PermissionManagementPage = lazyWithReload(
  () => import('@/features/adminCenter/screens/PermissionManagementPage')
);
const CreateUser = lazyWithReload(
  () => import('@/features/adminCenter/screens/CreateUser')
);
const BulkUploadUsers = lazyWithReload(
  () => import('@/features/adminCenter/screens/BulkUploadUsers')
);
const BulkUploadHistory = lazyWithReload(
  () => import('@/features/adminCenter/screens/BulkUploadHistory')
);
const CreateRole = lazyWithReload(
  () => import('@/features/adminCenter/screens/CreateRole')
);
const EditRole = lazyWithReload(
  () => import('@/features/adminCenter/screens/EditRole')
);
const CreatePermission = lazyWithReload(
  () => import('@/features/adminCenter/screens/CreatePermission')
);
const EditPermission = lazyWithReload(
  () => import('@/features/adminCenter/screens/EditPermission')
);
const AssignPermission = lazyWithReload(
  () => import('@/features/adminCenter/screens/AssignPermission')
);
const MainLayout = lazyWithReload(() => import('@/shared/layouts/MainLayout'));
const Home = lazyWithReload(() => import('@/features/home/screens/Home'));
const MessageUser = lazyWithReload(
  () => import('@/features/home/screens/MessageUser')
);
const AuthCallback = lazyWithReload(
  () => import('@/features/auth/screens/AuthCallbackPage')
);
const EsimQrPage = lazyWithReload(
  () => import('@/features/esimQr/screens/EsimQrPage')
);
const SetupPasswordPage = lazyWithReload(
  () => import('@/features/auth/screens/SetupPasswordPage')
);
const ConfigureSSOPage = lazyWithReload(
  () => import('@/features/auth/screens/ConfigureSSOPage')
);
const ResetPasswordPage = lazyWithReload(
  () => import('@/features/auth/screens/ResetPasswordPage')
);
const EditUser = lazyWithReload(
  () => import('@/features/adminCenter/screens/EditUser')
);
const ApiKeyManagementHome = lazyWithReload(
  () => import('@/features/api-key-management/screens/ApiKeyManagementHome')
);
const AssetAllocation = lazyWithReload(
  () => import('@/features/asset-allocation/screens/AssetAllocation')
);
const AvailableAssets = lazyWithReload(
  () => import('@/features/asset-allocation/components/Available-Assets')
);
const AllocationHistory = lazyWithReload(
  () => import('@/features/asset-allocation/components/AllocationHistory')
);
const Identities = lazyWithReload(
  () => import('@/features/identities/screens/Identities')
);
const EsimInventory = lazyWithReload(
  () => import('@/features/esim-inventory/screens/EsimInventory')
);

export const router = createBrowserRouter([
  // --------------------
  // Public routes
  // --------------------
  {
    path: 'login',
    element: (
      <PublicRoute>
        <LoginLayout />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
  },
  {
    path: 'verify-email',
    element: (
      <PublicRoute>
        <EsimQrPage />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
  },
  {
    path: 'setup-password',
    element: (
      <PublicRoute>
        <SetupPasswordPage />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
  },
  {
    path: 'reset-password',
    element: (
      <PublicRoute>
        <ResetPasswordPage />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
  },
  {
    path: 'auth/callback',
    element: (
      <PublicRoute>
        <AuthCallback />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
  },
  {
    path: 'sso-setup',
    element: (
      <PublicRoute>
        <ConfigureSSOPage />
      </PublicRoute>
    ),
    errorElement: <RouteError />,
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const token = url.searchParams.get('token')?.trim();

      if (!token) {
        throw new Response('Unauthorized. Invalid setup link.', {
          status: 401,
        });
      }

      try {
        const { enterpriseApi } =
          await import('@/features/enterprise/api/enterpriseApi');

        const response = await store.dispatch(
          enterpriseApi.endpoints.validateSsoToken.initiate({ token })
        );

        if (!response.data) {
          // Handle error response
          const errorMessage =
            (response.error as { message?: string })?.message ||
            'Failed to validate SSO token.';
          throw new Response(errorMessage, {
            status: 401,
          });
        }

        if (response.data.statusCode !== 200 || !response.data.data.isEnabled) {
          throw new Response(
            response.data.message ||
              'SSO validation failed or enterprise is disabled.',
            {
              status: 401,
            }
          );
        }

        return {
          token,
          enterpriseId: response.data.data.enterpriseId,
          enterpriseName: response.data.data.enterpriseName,
        };
      } catch (error) {
        if (error instanceof Response) {
          throw error;
        }
        throw new Response('Failed to validate SSO token.', { status: 401 });
      }
    },
  },
  // --------------------
  // Authenticated area
  // --------------------
  {
    element: (
      <ErrorBoundary>
        <ProtectedRoute />
      </ErrorBoundary>
    ),
    hydrateFallbackElement: <div aria-hidden />,
    errorElement: <RouteError />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            Component: Home,
          },
          {
            path: 'home',
            Component: Home,
          },
          {
            element: <ProtectedRoute permission={PERMISSIONS.SMS_VIEW} />,
            children: [
              {
                path: 'message-user',
                Component: MessageUser,
              },
            ],
          },
          {
            path: 'asset-allocation',
            Component: AssetAllocation,
            handle: {
              breadcrumb: [
                { label: 'Home', href: '/' },
                { label: 'Asset Allocation', isCurrent: true },
              ],
            },
            children: [
              {
                index: true,
                element: <Navigate to="available-assets" replace />,
              },
              {
                path: 'available-assets',
                Component: AvailableAssets,
              },
              {
                path: 'allocation-history',
                Component: AllocationHistory,
              },
            ],
          },
          {
            element: (
              <ProtectedRoute
                permission={PERMISSIONS.API_KEY_MANAGEMENT_VIEW}
              />
            ),
            children: [
              {
                path: 'api-key-management',
                Component: ApiKeyManagementHome,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'API Key Management', isCurrent: true },
                  ],
                },
              },
            ],
          },
          // -------- Permission-protected routes --------
          {
            element: (
              <ProtectedRoute
                permission={PERMISSIONS.ENTERPRISE_MANAGEMENT_VIEW}
              />
            ),
            children: [
              {
                path: 'enterprise-management',
                Component: EnterpriseManagement,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'Enterprise Management', isCurrent: true },
                  ],
                },
              },
            ],
          },
          {
            element: (
              <ProtectedRoute permission={PERMISSIONS.EDIT_ENTERPRISE} />
            ),
            children: [
              {
                path: 'enterprise-management/:id',
                Component: EnterpriseDetails,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    {
                      label: 'Enterprise Management',
                      href: '/enterprise-management',
                    },
                    { label: 'Details', isCurrent: true },
                  ],
                },
              },
              {
                path: 'enterprise-management/:id/edit',
                Component: EnterpriseDetails,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    {
                      label: 'Enterprise Management',
                      href: '/enterprise-management',
                    },
                    { label: 'Edit', isCurrent: true },
                  ],
                },
              },
            ],
          },

          {
            element: (
              <ProtectedRoute
                permission={PERMISSIONS.ENTERPRISE_ONBOARDING_VIEW}
              />
            ),
            children: [
              {
                path: 'enterprise-onboarding',
                Component: EnterpriseOnboarding,
                loader: async () => {
                  try {
                    const { enterpriseApi } =
                      await import('@/features/enterprise/api/enterpriseApi');

                    store.dispatch(setSkipTraceId(true));
                    try {
                      const [enterpriseTypesResult, loginMethodsResult] =
                        await Promise.allSettled([
                          store.dispatch(
                            enterpriseApi.endpoints.getEnterpriseTypes.initiate()
                          ),
                          store.dispatch(
                            enterpriseApi.endpoints.getLoginMethods.initiate()
                          ),
                        ]);

                      if (
                        enterpriseTypesResult.status === 'fulfilled' &&
                        enterpriseTypesResult.value.data &&
                        enterpriseTypesResult.value.data.length > 0
                      ) {
                        store.dispatch(
                          setEnterpriseTypes(enterpriseTypesResult.value.data)
                        );
                      } else {
                        console.error(
                          'Failed to fetch enterprise types:',
                          enterpriseTypesResult.status === 'rejected'
                            ? enterpriseTypesResult.reason
                            : 'No enterprise types returned'
                        );
                        store.dispatch(setEnterpriseTypes([]));
                        throw redirect('/enterprise-management');
                      }

                      if (
                        loginMethodsResult.status === 'fulfilled' &&
                        loginMethodsResult.value.data &&
                        loginMethodsResult.value.data.length > 0
                      ) {
                        store.dispatch(
                          setLoginMethods(loginMethodsResult.value.data)
                        );
                      } else {
                        console.error(
                          'Failed to fetch login methods:',
                          loginMethodsResult.status === 'rejected'
                            ? loginMethodsResult.reason
                            : 'No login methods returned'
                        );
                        store.dispatch(setLoginMethods([]));
                        throw redirect('/enterprise-management');
                      }

                      return null;
                    } finally {
                      store.dispatch(resetSkipTraceId());
                    }
                  } catch (error) {
                    if (error instanceof Response) {
                      throw error;
                    }

                    console.error(
                      'Failed to initialize onboarding data:',
                      error
                    );
                    store.dispatch(setEnterpriseTypes([]));
                    store.dispatch(setLoginMethods([]));
                    return null;
                  }
                },
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'Enterprise Onboarding', isCurrent: true },
                  ],
                },
              },
            ],
          },

          {
            element: (
              <ProtectedRoute permission={PERMISSIONS.IDENTITIES_VIEW} />
            ),
            children: [
              {
                path: 'provisioned-users',
                element: <Navigate to="portal-users" replace />,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'Provisioned Users', isCurrent: true },
                  ],
                },
              },
              {
                path: 'provisioned-users/:tab',
                Component: Identities,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'Provisioned Users', isCurrent: true },
                  ],
                },
              },
              {
                path: 'provisioned-users/:tab/:slcId/:iccid',
                Component: IdentityVaultDetails,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    {
                      label: 'Provisioned Users',
                      href: '/provisioned-users/portal-users',
                    },
                    { label: 'Details', isCurrent: true },
                  ],
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to="overview" replace />,
                  },
                  {
                    path: 'overview',
                    element: <OverviewTabWrapper />,
                  },
                  {
                    path: 'baseline-metrics',
                    element: <FullMetricsTabWrapper />,
                  },
                  {
                    path: 'live-metrics',
                    element: <LiveMetricsTabWrapper />,
                  },
                  {
                    path: 'manual-authentication',
                    element: <ManualAuthenticationTabWrapper />,
                  },
                  {
                    path: 'isolated-metrics',
                    element: <IsolatedMetricsTabWrapper />,
                  },
                ],
              },
            ],
          },

          {
            element: (
              <ProtectedRoute permission={PERMISSIONS.ESIM_INVENTORY_VIEW} />
            ),
            children: [
              {
                path: 'esim-inventory',
                Component: EsimInventory,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'eSIM Inventory', isCurrent: true },
                  ],
                },
              },
            ],
          },

          {
            element: <ProtectedRoute permission={PERMISSIONS.ADMIN_CENTER} />,
            loader: async () => {
              return null;
            },
            children: [
              {
                path: 'admin-center',
                Component: AdminCenterPage,
                handle: {
                  breadcrumb: [
                    { label: 'Home', href: '/' },
                    { label: 'Admin Center', isCurrent: true },
                  ],
                },
                children: [
                  {
                    path: 'user-management',
                    children: [
                      {
                        index: true,
                        Component: UserManagementPage,
                      },
                      {
                        path: 'create',
                        Component: CreateUser,
                        loader: async () => {
                          try {
                            const { assetAllocationApi } =
                              await import('@/features/asset-allocation/api/assetAllocationApi');

                            // Get current user from Redux store
                            const state = store.getState();
                            const currentUser = state.user?.currentUser;
                            const enterpriseId = currentUser?.enterpriseId;

                            // Only check if enterpriseId exists AND user is ESIM_OTP
                            if (
                              enterpriseId &&
                              currentUser?.loginMethodCode === 'ESIM_OTP'
                            ) {
                              store.dispatch(setSkipTraceId(true));
                              try {
                                const envType =
                                  (localStorage.getItem('envType') as
                                    | 'sandbox'
                                    | 'production') || 'production';
                                const assetsResult = await store.dispatch(
                                  assetAllocationApi.endpoints.getAssetsSummary.initiate(
                                    {
                                      enterprise_id: Number(enterpriseId),
                                      environment: envType,
                                    }
                                  )
                                );

                                // Check if unused_count is 0
                                const assetsSummary = assetsResult.data || [];
                                const unusedCount = assetsSummary?.[0]
                                  ?.unused_count
                                  ? parseInt(assetsSummary[0].unused_count, 10)
                                  : 0;

                                if (unusedCount === 0) {
                                  throw redirect(
                                    '/admin-center/user-management'
                                  );
                                }
                              } finally {
                                store.dispatch(resetSkipTraceId());
                              }
                            }

                            return null;
                          } catch (error) {
                            if (error instanceof Response) {
                              throw error;
                            }
                            // Don't block navigation on API errors
                            return null;
                          }
                        },
                      },
                      {
                        path: 'upload',
                        Component: BulkUploadUsers,
                      },
                      {
                        path: 'upload-history',
                        Component: BulkUploadHistory,
                      },
                      {
                        path: 'edit/:id',
                        loader: async ({ params }) => {
                          const { id } = params;
                          if (!id) {
                            throw new Response('User ID is required', {
                              status: 400,
                            });
                          }

                          const [
                            { userManagementApi },
                            { rolesApi },
                            { enterpriseApi },
                          ] = await Promise.all([
                            import('@/features/adminCenter/api/userManagementApi'),
                            import('@/features/adminCenter/api/rolesApi'),
                            import('@/features/enterprise/api/enterpriseApi'),
                          ]);

                          // Get permissions from Redux store
                          const state = store.getState();
                          const permissions = state.auth?.permissions || [];
                          const canViewEnterprises = permissions.includes(
                            PERMISSIONS.ENTERPRISE_MANAGEMENT_VIEW
                          );

                          // First, get the user
                          const userResult = await store.dispatch(
                            userManagementApi.endpoints.getUserById.initiate(id)
                          );

                          // Use isAdmin logic from UserManagementPage
                          // isAdmin = !currentUser?.enterpriseId (from Redux)
                          const currentUser = state.user?.currentUser;
                          const isAdmin = !currentUser?.enterpriseId;

                          const [rolesResult, enterprisesResult] =
                            await Promise.all([
                              store.dispatch(
                                rolesApi.endpoints.getRoles.initiate({
                                  page: 1,
                                  limit: 100,
                                  // Fetch all roles (active and inactive) for edit user
                                  // so inactive current role can be displayed
                                  ...(isAdmin
                                    ? {}
                                    : { role_type: 'enterprise_admin' }),
                                })
                              ),
                              canViewEnterprises
                                ? store.dispatch(
                                    enterpriseApi.endpoints.getEnterprises.initiate()
                                  )
                                : Promise.resolve({ data: [] }),
                            ]);

                          if (!userResult.data) {
                            throw new Response('User not found', {
                              status: 404,
                            });
                          }

                          return {
                            user: userResult.data,
                            roles: rolesResult.data ?? [],
                            enterprises: enterprisesResult.data ?? [],
                          };

                          if (!userResult.data) {
                            throw new Response('User not found', {
                              status: 404,
                            });
                          }

                          return {
                            user: userResult.data,
                            roles: rolesResult.data ?? [],
                            enterprises: enterprisesResult.data ?? [],
                          };
                        },
                        Component: EditUser,
                      },
                    ],
                  },
                  {
                    path: 'role-management',
                    children: [
                      {
                        index: true,
                        Component: RoleManagementPage,
                      },
                      {
                        element: (
                          <ProtectedRoute
                            permission={PERMISSIONS.ROLE_MANAGEMENT_CREATE}
                          />
                        ),
                        children: [
                          {
                            path: 'create',
                            Component: CreateRole,
                          },
                        ],
                      },
                      {
                        element: (
                          <ProtectedRoute
                            permission={PERMISSIONS.ROLE_MANAGEMENT_UPDATE}
                          />
                        ),
                        children: [
                          {
                            path: 'edit/:id',
                            Component: EditRole,
                          },
                          {
                            path: 'assign-permissions/:id',
                            Component: AssignPermission,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'permission-management',
                    children: [
                      {
                        index: true,
                        Component: PermissionManagementPage,
                      },
                      {
                        element: (
                          <ProtectedRoute
                            permission={
                              PERMISSIONS.PERMISSION_MANAGEMENT_CREATE
                            }
                          />
                        ),
                        children: [
                          {
                            path: 'create',
                            Component: CreatePermission,
                          },
                        ],
                      },
                      {
                        element: (
                          <ProtectedRoute
                            permission={
                              PERMISSIONS.PERMISSION_MANAGEMENT_UPDATE
                            }
                          />
                        ),
                        children: [
                          {
                            path: 'edit/:id',
                            Component: EditPermission,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
